import {
  ServiceStatus,
  ServiceAvailability,
  type PartnerServicesFilters,
} from "@/types/partner-settings";

export type GuesteriaServicesFiltersState = {
  searchQuery: string;
  selectedTags: number[];
  statuses: ServiceStatus[];
  minPrice?: number;
  maxPrice?: number;
  isRecommended?: boolean;
  availability?: ServiceAvailability;
};

export const DEFAULT_GUESTERIA_SERVICES_FILTERS: GuesteriaServicesFiltersState =
  {
    searchQuery: "",
    selectedTags: [],
    statuses: [ServiceStatus.ACTIVE],
    minPrice: undefined,
    maxPrice: undefined,
    isRecommended: undefined,
    availability: undefined,
  };

export const SERVICE_STATUS_OPTIONS = [
  ServiceStatus.ACTIVE,
  ServiceStatus.INACTIVE,
] as const;

export const AVAILABILITY_OPTIONS = [
  ServiceAvailability.WHOLE_WEEK,
  ServiceAvailability.WEEKDAYS,
  ServiceAvailability.WEEKENDS,
] as const;

/**
 * Maps the UI filter state to the API filter shape the services endpoint expects.
 * The two shapes intentionally diverge (`searchQuery`→`search`, `selectedTags`→`tagIds`,
 * `statuses`→`status`); without this mapping those three filters are silently dropped
 * because `PartnerServicesFilters` is all-optional. Empty search/arrays are omitted so
 * the backend treats them as "no filter" rather than an empty selection.
 */
export function toPartnerServicesFilters(
  state: GuesteriaServicesFiltersState,
): PartnerServicesFilters {
  const search = state.searchQuery.trim();

  return {
    search: search.length > 0 ? search : undefined,
    tagIds: state.selectedTags.length > 0 ? state.selectedTags : undefined,
    status: state.statuses.length > 0 ? state.statuses : undefined,
    minPrice: state.minPrice,
    maxPrice: state.maxPrice,
    isRecommended: state.isRecommended,
    availability: state.availability,
  };
}

export function countActiveServicesFilters(
  filters: GuesteriaServicesFiltersState,
): number {
  return (
    (filters.searchQuery.trim() ? 1 : 0) +
    filters.selectedTags.length +
    (filters.statuses.length < SERVICE_STATUS_OPTIONS.length ? 1 : 0) +
    (filters.minPrice !== undefined || filters.maxPrice !== undefined ? 1 : 0) +
    (filters.isRecommended ? 1 : 0) +
    (filters.availability ? 1 : 0)
  );
}
